import { useCallback, useEffect, useRef, type RefObject } from 'react';
import type { NativeScrollEvent, NativeSyntheticEvent } from 'react-native';
import { useRetainedRef, type RetainedToken } from './retainedState';
import { useSafeTimeout } from './useSafeTimeout';

/**
 * ui-kit GoodsCarousel 이 imperative handle 로 노출하는 shape.
 *
 * ⚠️ `ref` prop 의 선언 타입(GridViewRef)에는 scrollToOffset 이 빠져 있지만 GoodsCarousel 의
 * useImperativeHandle 은 둘 다 노출한다(GoodsListRef 가 실제 shape). GridViewRef 와 호환되게
 * scrollToIndex 도 포함해 둔다.
 */
type CarouselScrollHandle = {
  scrollToIndex: (index: number) => void;
  scrollToOffset: (params: { offset: number; animated?: boolean }) => void;
};

// 배치 렌더로 콘텐츠 폭이 자라는 것을 따라가기 위한 재시도 간격 / 최대 횟수.
const RETRY_MS = 120;
const MAX_ATTEMPTS = 8;
// 도달 판정 및 "진전 없음" 판정 허용 오차(px).
const OFFSET_EPSILON = 2;

/**
 * 가로 캐러셀의 스크롤 위치를 visit(retainedState) 단위로 저장/복원한다.
 *
 * InactiveScreenGate 로 화면 콘텐츠가 unmount→remount 되면(같은 visit) 캐러셀이 맨 앞으로
 * 되돌아가 사용자가 옆으로 넘겨둔 위치를 잃는다. CoverImages(coverIndexSlot + contentOffset)와
 * 같은 취지인데, ui-kit GoodsCarousel 은 `contentOffset` prop 이 없고 ref(scrollToOffset)만
 * 노출하므로 선언적 복원 대신 마운트 후 명령형 복원을 한다.
 *
 * - 저장: onScroll 로 수평 offset 을 retained 슬롯에 기록(리렌더 없음).
 * - 복원: 데이터가 준비된 뒤 scrollToOffset. 콘텐츠가 배치로 자라는 것을 따라가며 재시도하고,
 *   도달하거나 더 이상 진전이 없으면 멈춘다.
 *
 * ⚠️ **호출부에서 `windowSize` 를 좁히면 안 된다.** FlatList 의 콘텐츠 폭은 렌더된 셀까지이고
 * 렌더 윈도우는 windowSize 로 제한되므로, 저장 offset 이 그 밖이면 scrollToOffset 이 클램프되고
 * → 오프셋이 안 나가고 → 윈도우도 안 밀리고 → 콘텐츠도 안 자라는 교착이 된다(리스트 끝쪽
 * 위치에서 복원이 실패하던 원인). 기본값(21)이면 현실적인 캐러셀 길이는 모두 도달 가능하다.
 */
export const useCarouselScrollRestore = (params: {
  token: RetainedToken<number>;
  /** 리스트에 복원할 데이터가 준비됐는지. false 면 준비될 때까지 복원을 미룬다. */
  ready: boolean;
  /**
   * 이미 ref 를 쓰고 있는 호출부는 그 ref 를 넘긴다(엘리먼트에 ref 는 하나만 붙일 수 있으므로).
   * 미지정이면 훅이 만든 ref 를 반환한다.
   */
  listRef?: RefObject<CarouselScrollHandle | null>;
}) => {
  const { token, ready, listRef: externalListRef } = params;
  const savedOffset = useRetainedRef(token);
  const setSafeTimeout = useSafeTimeout();

  const internalListRef = useRef<CarouselScrollHandle | null>(null);
  const listRef = externalListRef ?? internalListRef;
  // mount 시점 스냅샷 — 이후 onScroll 저장값에 흔들리지 않게 복원 목표를 고정한다.
  const targetRef = useRef(savedOffset.current);
  // 관측된 실제 offset. 스크롤 이벤트를 아직 못 받은 상태를 NaN 으로 구분한다
  // (저장값으로 초기화하면 첫 판정에서 "이미 도달"로 오인해 재시도가 돌지 않는다).
  const lastOffsetRef = useRef(Number.NaN);
  const startedRef = useRef(false);
  const restoredRef = useRef(false);

  const onScroll = useCallback(
    (event: NativeSyntheticEvent<NativeScrollEvent>) => {
      const offset = event.nativeEvent.contentOffset.x;
      lastOffsetRef.current = offset;
      // 복원이 끝나기 전엔 저장하지 않는다. 복원 중 클램프된 중간 offset 이 저장되면
      // remount 가 반복될 때 저장 위치가 점점 앞으로 밀린다.
      if (!restoredRef.current) return;
      savedOffset.current = offset;
    },
    [savedOffset],
  );

  useEffect(() => {
    if (startedRef.current) return;
    if (!ready) return;
    startedRef.current = true;

    const target = targetRef.current;
    if (target <= 0) {
      restoredRef.current = true;
      return;
    }

    const attempt = (count: number, prevAchieved: number) => {
      listRef.current?.scrollToOffset({ offset: target, animated: false });

      if (count >= MAX_ATTEMPTS) {
        restoredRef.current = true;
        return;
      }

      setSafeTimeout(() => {
        const achieved = lastOffsetRef.current;
        const reached = Math.abs(achieved - target) <= OFFSET_EPSILON;
        // 클램프 상한이 그대로면 콘텐츠가 더 자라지 않은 것 → 더 밀어도 의미 없다.
        const stalled = Math.abs(achieved - prevAchieved) <= OFFSET_EPSILON;
        if (reached || stalled) {
          restoredRef.current = true;
          return;
        }
        attempt(count + 1, achieved);
      }, RETRY_MS);
    };

    // 레이아웃이 한 번 돌고 난 뒤 시작한다.
    requestAnimationFrame(() => attempt(0, Number.NaN));
  }, [ready, setSafeTimeout, listRef]);

  return { listRef, onScroll };
};